import {Component, computed, effect, HostListener, inject, isDevMode, OnDestroy, OnInit, signal} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {LoginService} from './core/login.service';
import {UiToolsService} from './core/ui-tools.service';
import {CmsApiService} from './core/cms-api.service';
import {CmsErrorHandlerService} from './core/cms-error-handler.service';
import {CmsQueueService} from './core/cms-queue.service';
import {TranslateService} from '@ngx-translate/core';
import {CommonsService} from './core/commons.service';
import {Subscription} from 'rxjs';
import {UserData} from './core/definitions/user-data';
import {SwUpdate} from '@angular/service-worker';
import {PrimusBackendInstanceService} from './core/primus-backend-instance.service';
import {SearchPageService} from './object-search/search-page.service';
import {JobStatusSubscriberService} from './core/jobstatussubcriber/job-status-subscriber.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ClientConfig} from './core/definitions/client-config';
import {PrimusRouterService} from './core/primus-router.service';
import {PrimusRouteService} from './core/primus-route.service';
import {distinctUntilChanged, take} from "rxjs/operators";
import {MatDialog} from "@angular/material/dialog";
import {AlertDialogComponent} from "./shared/alert-dialog/alert-dialog.component";
import {ConfirmDialogData} from "./object-edit/confirm-dialog/confirm-dialog.component";
import {SettingsService} from "./core/settings.service";
import {FieldConditionService} from "./core/field-condition.service";
import {ValueOptionService} from "./core/value-option.service";
import {FieldDateInfoService} from "./core/field-date-info.service";
import {FeatureFlagsService} from "./core/feature-flags.service";
import {SearchReferenceService} from "./core/search-reference.service";
import {HttpClient} from "@angular/common/http";
import {ModelStore} from "./core/model-store/model-store";
import {ReportUserGeneratedAdapter} from "./core/model-store/adapters/report-user-generated-adapter";
import {ReportViewTypeAdapter} from "./core/model-store/adapters/report-view-type-adapter";
import {ReportUserGenerated} from "./core/model-store/models/report-user-generated";
import {ReportUserTemplate} from "./core/model-store/models/report-user-template";
import {ReportViewType} from "./core/model-store/models/report-view-type";
import {OperationModel} from "./core/model-store/models/operation";
import {OperationStatus} from "./core/model-store/models/operation-status";
import {ReportUserTemplateAdapter} from "./core/model-store/adapters/report-user-template-adapter";
import {OperationStatusAdapter} from "./core/model-store/adapters/operation-status-adapter";
import {OperationAdapter} from "./core/model-store/adapters/operation-adapter";
import {HibernationService} from "./shared/hibernation.service";
import {HibernationFlowService} from "./shared/hibernation-flow.service";
import {Router} from "@angular/router";

const CHECK_SW_INTERVAL_MS = 15 * 60 * 1000;

interface ModelResponse {
  entrypoints: string[];
  files: {
    string: string
  }
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly MUSEUM_SELECTOR_PARAM = 'selectMuseum';

  // Injections
  primusRouter: PrimusRouterService = inject(PrimusRouterService);
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly cmsErrorHandlerService: CmsErrorHandlerService = inject(CmsErrorHandlerService);
  private readonly cmsQueue: CmsQueueService = inject(CmsQueueService);
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly document: Document = inject(DOCUMENT);
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly fieldConditions: FieldConditionService = inject(FieldConditionService);
  private readonly fieldDateInfo: FieldDateInfoService = inject(FieldDateInfoService);
  private readonly hibernationService = inject(HibernationService);
  private readonly hibernationFlow = inject(HibernationFlowService);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly jobStatusSubscriberService: JobStatusSubscriberService = inject(JobStatusSubscriberService);
  private readonly loginService: LoginService = inject(LoginService);
  private readonly modalService: MatDialog = inject(MatDialog);
  private readonly modelStore: ModelStore = inject(ModelStore);
  private readonly primusBackendInstanceService: PrimusBackendInstanceService = inject(PrimusBackendInstanceService);
  private readonly primusRoute: PrimusRouteService = inject(PrimusRouteService);
  private readonly router: Router = inject(Router);
  private readonly searchPageService: SearchPageService = inject(SearchPageService);
  private readonly searchReferenceService: SearchReferenceService = inject(SearchReferenceService);
  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly swUpdate: SwUpdate = inject(SwUpdate);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly uiTools: UiToolsService = inject(UiToolsService);
  private readonly valueOptions: ValueOptionService = inject(ValueOptionService);

  public currentStateName;
  public authErr: string;
  public txtLoadingContents: string;
  public txtToLogin: string;
  public hideLoader: boolean;

  private txtApiAuthenticationFailed: string;
  private txtNewVersionAvailable: string;
  private txtNewVersionDownload: string;

  private user: UserData;
  private authState = {
    user: false,
    api: false
  };
  private apiAuthRetryCounter = 0;
  private readonly apiAuthRetryMaxCount = 30;
  private readonly apiAuthRetryMs = 1000;
  private apiAuthRetryTimer: ReturnType<typeof setTimeout> | null = null;
  // Auth state can emit multiple times while earlier async steps are still running.
  // This counter gives each auth run a token so older (stale) runs cannot update UI/state
  // after a newer auth run has already started.
  // TODO: Remove when auth flow is refactored to a single cancellable/serialized pipeline.
  private authFlowRunVersion = 0;
  private authParamsStripped = false;
  // Ensure expensive settings bootstrap runs once per successful API-auth session.
  private applicationSettingsLoaded = false;
  // Cached "has multiple museums" decision for wake-failure routing.
  // We keep both a resolved value and an inflight promise:
  // - value cache avoids repeating the request after it has completed
  // - promise cache deduplicates concurrent calls while the request is still running
  private knownUserHasMultipleMuseums: boolean | null = null;
  private knownUserHasMultipleMuseumsPromise: Promise<boolean> | null = null;
  private showWakeFailureSelectorLink = signal(false);

  showApiSelector = this.hibernationFlow.backendSelectorVisible;
  // Keep splash visible for the full wake lifecycle:
  // - while wake UI is active
  // - and while a long-wake or wake-error message is present.
  // This avoids short splash flashes for very quick wake checks.
  // Museum selector has priority and should never be blocked by wake splash.
  waitForHibernation = computed(() =>
    !this.showApiSelector() && (this.hibernationService.isWakeFlowUiActive() || !!this.hibernationService.wakeupError())
  );
  wakeFlowActive = this.hibernationService.isWakeFlowActive;
  wakeFailureInfoMessageKey = computed(() => this.showWakeFailureSelectorLink()
    ? 'TRANS__APP_COMPONENT__WAKE_TIMEOUT_SELECTOR_PROMPT'
    : null);
  wakeFailureActionLabelKey = computed(() => this.showWakeFailureSelectorLink()
    ? 'TRANS__APP_COMPONENT__GO_TO_MUSEUM_SELECTOR'
    : null);
  private newVersionAvailable: boolean;

  private readonly subscription: Subscription = new Subscription();

  className: string;
  private languageSet = false;

  showFaq: boolean = false;

  // Loading state
  loadingUser = signal(true);
  loadingData = signal(true);
  checkingVersion = signal(false);
  checkingPermissions = signal(true);
  currentLoadingSteps = computed(() => {
    if (this.loadingUser()) {
      return ["TRANS__SPLASHSCREEN_LOADING_USER"]
    }
    if (this.checkingPermissions()) {
      return ["TRANS__SPLASHSCREEN_CHECKING_PERMISSIONS"]
    }
    // this step could take some time, so we make up some messages to show the user that actual work is happening.
    if (this.loadingData()) {
      return [
        "TRANS__SPLASHSCREEN_LOADING_DATA_1",
        "TRANS__SPLASHSCREEN_LOADING_DATA_2",
        "TRANS__SPLASHSCREEN_LOADING_DATA_3",
        "TRANS__SPLASHSCREEN_LOADING_DATA_4",
        "TRANS__SPLASHSCREEN_LOADING_DATA_5",
        "TRANS__SPLASHSCREEN_LOADING_DATA_6",
        "TRANS__SPLASHSCREEN_LOADING_DATA_7",
        "TRANS__SPLASHSCREEN_LOADING_DATA_8",
      ]
    }

    return null
  })

  private checkSwVersionInterval = null;

  constructor() {

    this.modelStore.registerModelFor('report_user_generated', 'report_user_generated', ReportUserGenerated);
    this.modelStore.registerAdapterFor('report_user_generated', new ReportUserGeneratedAdapter<ReportUserGenerated>('report_user_generated', 'report_user_generated'));

    this.modelStore.registerModelFor('report_view_type', 'report_view_types', ReportViewType);
    this.modelStore.registerAdapterFor('report_view_type', new ReportViewTypeAdapter<ReportViewType>('report_view_type', 'report_view_types'));

    this.modelStore.registerModelFor('report_user_template', 'report_user_templates', ReportUserTemplate);
    this.modelStore.registerAdapterFor('report_user_template', new ReportUserTemplateAdapter<ReportUserTemplate>('report_user_template', 'report_user_templates'));

    this.modelStore.registerModelFor('operation_status', 'operation_status', OperationStatus);
    this.modelStore.registerAdapterFor('operation_status', new OperationStatusAdapter('operation_status', 'operation_status'));

    this.modelStore.registerModelFor('operation', 'operations', OperationModel);
    this.modelStore.registerAdapterFor('operation', new OperationAdapter('operation', 'operations'));

    // Updates className with the latest className, updates only on change. This prevents NG0100 Error
    this.subscription.add(
      this.primusRouter.classNameObservable
        .pipe(distinctUntilChanged())
        .subscribe(className => {
          this.className = className;
        })
    )
    this.primusBackendInstanceService.responseStatusSubject
      .pipe(distinctUntilChanged())
      .subscribe((res: Response) => {

        // Reacts to 503 status.
       if(res.status === 503){
         const modalRef = this.modalService.open(AlertDialogComponent, {
           panelClass: 'alert-dialog',
           data: {
             modalTitle: 'TRANS__APP_COMPONENT__SERVICE_UNAVAILABLE',
             modalContent: 'TRANS__EXCEPTION_DEFAULT_503',
             modalButton: 'TRANS__ALERT_DIALOG__RELOAD',
           } as ConfirmDialogData
         })

         modalRef.afterClosed()
           .pipe(
             take(1),
           ).subscribe(() => {
             window.location.reload();
         })
       }
    });

    if (!window['modelViewer']) {
      this.httpClient.get<ModelResponse>('https://modelviewer.ekultur.org/asset-manifest.json').subscribe(result => {
        result.entrypoints.forEach((entrypoint) => {
          if (entrypoint.endsWith('.js')) {
            const scriptElement = document.createElement('script');
            scriptElement.id = `modelviewer-microfrontend-${entrypoint}`;
            scriptElement.src = `https://modelviewer.ekultur.org/${entrypoint}`;
            document.head.appendChild(scriptElement);
          }
        });
      })
    }
  }

  private checkSwVersion() {
    if (this.swUpdate.isEnabled) {
      this.checkingVersion.set(true);
      this.swUpdate.checkForUpdate().then(() => {
        this.checkingVersion.set(false);
        if (this.newVersionAvailable) {
          this.showVersionSnackBar();
        }
      });
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.uiTools.setWindowSize();
  }

  ngOnInit(): void {
    this.setSomeTranslations();

    this.primusBackendInstanceService.authError.subscribe(err => {
      this.setBackendSelectorVisible(false);
      this.authErr = err;
    });

    this.registerAuthSubscriber();
    this.registerCurrentUserSubscriber();

    if (this.apiAuthRetryCounter < this.apiAuthRetryMaxCount) {
      this.primusBackendInstanceService.setRefreshTokenSetAuthenticated().then();
    }

    this.checkSwVersion()
    this.checkSwVersionInterval = setInterval(() => {
      this.checkSwVersion()
    }, CHECK_SW_INTERVAL_MS)
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    clearInterval(this.checkSwVersionInterval);
    if (this.apiAuthRetryTimer) {
      clearTimeout(this.apiAuthRetryTimer);
      this.apiAuthRetryTimer = null;
    }
  }

  showFaqSidebar(show: boolean) {
    this.showFaq = show;
  }

  private setBackendSelectorVisible(visible: boolean) {
    this.hibernationFlow.setBackendSelectorVisible(visible);
  }

  private async getApplicationSettings(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.authState?.api) {
        resolve();
        return;
      }
      this.cmsQueue.runCmsFnWithQueue(this.cms.getClientConfig, undefined, false,
        (data: ClientConfig) => {
          // eslint-disable-next-line no-console
          if (isDevMode()) {
            console.debug('[APP] -- cms.getClientConfig() complete');
          }
          if (this.primusRoute.params.lang) {
            this.setTranslationLanguage(this.primusRoute.params.lang)
          } else {
            this.setTranslationLanguage(data.CLIENT_LANGUAGE);
          }
          this.commons.setObjectTypes(data.OBJECT_TYPES);
          if (data.PATH_SEPARATOR_SYMBOL) {
            this.commons.setPathSeparatorSymbol(data.PATH_SEPARATOR_SYMBOL);
          }
          this.settingsService.initSettings();
          this.featureFlagsService.initFeatureFlags();
          this.searchPageService.checkCreateSearchContainer();
          this.setSomeTranslations();
          this.fieldConditions.setFieldConditions();
          this.valueOptions.setAllValueOptions();
          this.fieldDateInfo.setFieldDateInfo();
          this.searchReferenceService.setSearchReferences();
          resolve();
        },
        (e: Error) => {
          reject(e);
        });
    });
  }

  private setTranslationLanguage(language) {
    this.translate.use(language);
    this.languageSet = true;

    this.document.documentElement.lang = language;
  }

  hasMainMenu() : boolean {
    const noMenuStates = ['authenticate', 'home.primus.full-screen', 'home.primus.reportPreview.report_id']; // , 'home.primus.annotate'

    if (this.primusRouter.currentState() === 'home.primus.reportPreview.report_id') {
      let bodyElement = this.document.getElementsByTagName('body')[0];
      bodyElement.classList.add('no-fixed-height');
      bodyElement.classList.add('report-preview-body');
    }
    return this.user && (!this.primusRouter.currentState() || noMenuStates.indexOf(this.primusRouter.currentState()) === -1);
  }

  isReportPreview() : boolean {
    if (this.primusRouter.currentState() === 'home.primus.reportPreview.report_id') {
      let bodyElement = this.document.getElementsByTagName('body')[0];
      if (!bodyElement.classList.contains('report-preview-body')) {
        bodyElement.classList.add('report-preview-body');
      }
    }

    return this.primusRouter.currentState() === 'home.primus.reportPreview.report_id';
  }

  registerClick(event) {
    this.uiTools.registerDocumentClick(event);
  }

  // showAuthErr(err: string) {
  //   return err;
  // }

  isAuthenticated() {
    return this.authState.user;
  }

  private init() {
    // eslint-disable-next-line no-console
    if (isDevMode()) {
      console.debug('[APP] -- init()');
    }

    this.currentStateName = this.primusRouter.currentState();
    this.subscription.add(
      this.primusRouter.navigationHandler(() => {
        this.currentStateName = this.primusRouter.currentState();
      })
    );

    this.initServiceWorker();

    this.initApp().then();
  }

  private showVersionSnackBar() {
    const versionSnackBar = this.snackBar.open(this.txtNewVersionAvailable, this.txtNewVersionDownload, {
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });

    versionSnackBar.onAction().subscribe(() => {
      window.location.reload();
    });

  }

  private initServiceWorker() {
    try {
      console.log('[SW] Service-worker enabled: ', this.swUpdate.isEnabled);
      if (this.swUpdate.isEnabled) {
        this.swUpdate.checkForUpdate().then(updateFound => {
          console.log('[SW] Update available: ', updateFound);
          this.newVersionAvailable = updateFound;
        });
      }
    } catch (e) {
      console.error('[SW] Unable to subscribe to service-worker registration', e);
    }
  }

  private async initApp() {
    /* eslint-disable no-console */
    if (isDevMode()) {
      console.debug('[APP] -- initApp()');
    }

    try {
      await this.loginService.getUserData();
    } catch (e) {
      console.error(e);
      this.authErr = e.error?.message ?? 'An unknown error occurred';
      return;
    }

    if (isDevMode()) {
      console.debug('[APP] -- models.getModelsAsync() complete');
    }
    await this.setPostLoginState();

    if (isDevMode()) {
      console.debug('[APP] -- post login state set');
    }

    this.jobStatusSubscriberService.startPolling();
  } /* eslint-enable no-console */

  private async setPostLoginState() {
    this.loadingData.set(false);

    if (this.newVersionAvailable) {
      this.showVersionSnackBar();
    }
  }

  private setSomeTranslations() {
    if (!this.languageSet) {
      let lang = 'no';
      if (navigator.language.toLowerCase().includes('sv')) {
        lang = 'sv';
      }
      this.translate.use(lang);
    }

    this.translate.get('TRANS__APP_COMPONENT__LOADING_CONTENTS').subscribe(s => {
      this.txtLoadingContents = s;
    });

    this.translate.get('TRANS__APP_COMPONENT__AUTHENTICATION_FAILED').subscribe(s => {
      this.txtApiAuthenticationFailed = s;
    });

    this.translate.get('TRANS__APP_COMPONENT__NEW_VERSION_AVAILABLE').subscribe(s => {
      this.txtNewVersionAvailable = s;
    });

    this.translate.get('TRANS__APP_COMPONENT__NEW_VERSION_DOWNLOAD').subscribe(s => {
      this.txtNewVersionDownload = s;
    });

    this.translate.get('TRANS__APP_COMPONENT__BACK_TO_LOGIN').subscribe(s => {
      this.txtToLogin = s;
    });
  }

  private retryApiAuth() {
    const museum = PrimusBackendInstanceService.getInstanceDetails();
    if (!museum) {
      this.setBackendSelectorVisible(true);
      return;
    }
    if (this.apiAuthRetryCounter > this.apiAuthRetryMaxCount) {
      // Keep the selected backend and continue retrying; backend may still be waking.
      const museumName = museum?.name ?? 'Backend';
      this.authErr = `${museumName} - ${this.txtApiAuthenticationFailed}`;
      this.apiAuthRetryCounter = 0;
      if (this.apiAuthRetryTimer) {
        clearTimeout(this.apiAuthRetryTimer);
      }
      this.apiAuthRetryTimer = setTimeout(() => {
        this.primusBackendInstanceService.apiAuthenticate(museum);
      }, this.apiAuthRetryMs);
    } else {
      try {
        if (this.apiAuthRetryCounter > 0) {
          if (isDevMode()) {
            console.log(`[APP] -- Retrying API-auth in ${this.apiAuthRetryMs}, attempt: ${this.apiAuthRetryCounter}`);
          }
          if (this.apiAuthRetryTimer) {
            clearTimeout(this.apiAuthRetryTimer);
          }
          this.apiAuthRetryTimer = setTimeout(() => {
            this.primusBackendInstanceService.apiAuthenticate(museum);
          }, this.apiAuthRetryMs);
        } else {
          this.primusBackendInstanceService.apiAuthenticate(museum);
        }
        this.apiAuthRetryCounter++;
      } catch (e) {
        console.error(e);
      }
    }
  }

  private registerCurrentUserSubscriber() {
    this.loginService.currentUser.subscribe(async (userData) => {
      if (!userData) {
        return;
      }

      this.user = userData;

      if (isDevMode()) {
        // eslint-disable-next-line no-console
        console.debug('[APP] -- currentUserObservable triggered');
      }

      // Attempt to initialize component only when user data has been loaded.
      this.cms.init((response, params) => {
        this.cmsErrorHandlerService.errHandler(response, params);
      });

      if (isDevMode()) {
        // eslint-disable-next-line no-console
        console.debug('[APP] -- cms.init() complete');
      }
    });
  }

  private registerAuthSubscriber() {
    this.primusBackendInstanceService.authenticated.subscribe(async (state) => {
      const isStaleRun = this.beginAuthFlowRunGuard();
      // 1) Keep splash/loading indicators in sync with raw auth state.
      this.loadingUser.set(!state.user)
      this.checkingPermissions.set(!state.api);
      if (!state.user && !state.api) {
        return;
      }

      /* eslint-disable no-console */
      if (isDevMode()) {
        console.debug(`[APP] -- user authenticated: ${state.user}`);
        console.debug(`[APP] -- api authenticated: ${state.api}`);
        console.debug(`[APP] -- api auth failed: ${this.authErr}`);
      }
      /* eslint-enable no-console */

      this.authState = state;
      const forceApiSelector = this.isBackendSelectorForced();
      if (state.api) {
        if (this.apiAuthRetryTimer) {
          clearTimeout(this.apiAuthRetryTimer);
          this.apiAuthRetryTimer = null;
        }
        this.authErr = '';
        this.apiAuthRetryCounter = 0;
        this.showWakeFailureSelectorLink.set(false);
      }
      if (!state.api) {
        this.applicationSettingsLoaded = false;
      }
      if (state.user) {
        this.stripAuthParams();
      }
      if (state.api && !forceApiSelector) {
        this.stripMuseumSelectorParam();
      }

      const hasUser = !!state.user;
      const hasApi = !!state.api;
      const hasMuseum = !!PrimusBackendInstanceService.getInstanceDetails();
      const wakeupError = this.hibernationService.wakeupError();

      // Forced selector mode (selectMuseum=1) should win over wake/ready state
      // once user auth exists, so users can always switch museum explicitly.
      if (forceApiSelector && hasUser) {
        this.enterForcedSelectorMode();
        return;
      }

      const shouldShowWakeFailureSelector = hasUser
        && !hasApi
        && hasMuseum
        && !!wakeupError;

      // 2) Long-wait wake handling:
      // This message means wake is taking longer than expected right now, not terminal failure.
      // - multi-museum users: show guidance text + selector link on splash
      // - single-museum users: stay in wake flow and keep retrying
      if (shouldShowWakeFailureSelector) {
        this.authErr = wakeupError;
        if (this.apiAuthRetryTimer) {
          clearTimeout(this.apiAuthRetryTimer);
          this.apiAuthRetryTimer = null;
        }
        const routeToSelector = await this.shouldRouteWakeFailureToSelector(forceApiSelector);
        if (isStaleRun()) {
          return;
        }
        if (routeToSelector) {
          this.setBackendSelectorVisible(false);
          this.showWakeFailureSelectorLink.set(true);
          this.apiAuthRetryCounter = 0;
          // Keep wake/auth retries running while link is visible so users can wait here
          // and continue automatically when backend wakes up.
          this.retryApiAuth();
          return;
        }
        // Single-museum users should stay in wake flow (splash) and keep retrying.
        this.setBackendSelectorVisible(false);
        this.showWakeFailureSelectorLink.set(false);
        // Keep long-wake guidance visible on splash while retry loop is running.
        // The wake service clears the error itself once wake succeeds.
        this.retryApiAuth();
        return;
      }
      this.showWakeFailureSelectorLink.set(false);

      const isReadyToInitialize = hasUser && hasApi && hasMuseum;
      // 3) Fully authenticated and backend selected: initialize app and settings once.
      if (isReadyToInitialize) {
        this.setBackendSelectorVisible(false);
        if (!this.hideLoader) {
          this.hideLoader = true;
          this.init();
        }

        const shouldFetchSettings = !forceApiSelector && !this.applicationSettingsLoaded;
        if (shouldFetchSettings) {
          try {
            await this.getApplicationSettings();
            if (isStaleRun()) {
              return;
            }
            this.applicationSettingsLoaded = true;
          } catch (e) {
            if (isStaleRun()) {
              return;
            }
            this.applicationSettingsLoaded = false;
            this.authErr = e.error?.message ?? 'An unknown error occurred';
          }
        }
        return;
      }

      const shouldShowApiSelector =
        hasUser && !hasApi && !hasMuseum;

      // 4) Selector flow: explicit selector mode, or no selected backend yet.
      if (shouldShowApiSelector) {
        this.setBackendSelectorVisible(true);
        this.apiAuthRetryCounter = 0;
        return;
      }

      const shouldRetryApiAuth = !forceApiSelector && hasUser && !hasApi && hasMuseum;
      // 5) Default fallback: backend selected but API auth missing -> retry API auth.
      if (shouldRetryApiAuth) {
        this.setBackendSelectorVisible(false);
        this.retryApiAuth();
      }
    });
  }

  // Force selector mode for ?selectMuseum=1.
  // In this mode we clear active museum context and stop background retry/polling
  // so no museum-specific requests continue until user explicitly selects a museum.
  private enterForcedSelectorMode() {
    this.showWakeFailureSelectorLink.set(false);
    this.setBackendSelectorVisible(true);
    this.apiAuthRetryCounter = 0;
    if (this.apiAuthRetryTimer) {
      clearTimeout(this.apiAuthRetryTimer);
      this.apiAuthRetryTimer = null;
    }
    this.hibernationService.clearWakeupError();
    this.jobStatusSubscriberService.stopPolling();
    PrimusBackendInstanceService.cleanInstanceDetails();
    this.primusBackendInstanceService.setApiAuthenticated(false);
    this.authState = {
      ...this.authState,
      api: false,
    };
  }

  // Auth events can overlap while async auth steps are running.
  // This returns a guard fn used after await-points to skip stale/outdated runs.
  private beginAuthFlowRunGuard(): () => boolean {
    // Capture token for this auth run. If a newer run starts, this one becomes stale.
    const runVersion = ++this.authFlowRunVersion;
    // Caller checks this after await points to avoid applying outdated async results.
    return () => runVersion !== this.authFlowRunVersion;
  }

  private isBackendSelectorForced(): boolean {
    const value = this.primusRoute.params?.[AppComponent.MUSEUM_SELECTOR_PARAM];
    return value === '1' || value === 'true';
  }

  openMuseumSelectorFromWakeFailure() {
    this.showWakeFailureSelectorLink.set(false);
    this.router.navigate([], {
      queryParams: {
        [AppComponent.MUSEUM_SELECTOR_PARAM]: '1',
      },
      queryParamsHandling: 'merge',
    });
    this.setBackendSelectorVisible(true);
  }

  // Decide post-wake-failure UX:
  // - multi-museum users (or forced selector mode) are offered a selector link
  // - single-museum users stay in wake/retry flow for current backend
  private async shouldRouteWakeFailureToSelector(forceApiSelector: boolean): Promise<boolean> {
    if (forceApiSelector) {
      return true;
    }
    return this.getKnownUserHasMultipleMuseums();
  }

  private async getKnownUserHasMultipleMuseums(): Promise<boolean> {
    if (this.knownUserHasMultipleMuseums !== null) {
      return this.knownUserHasMultipleMuseums;
    }
    if (!this.knownUserHasMultipleMuseumsPromise) {
      this.knownUserHasMultipleMuseumsPromise = this.primusBackendInstanceService.getUserPrimusServers()
        .then((servers) => {
          const hasMultipleMuseums = Array.isArray(servers) && servers.length > 1;
          this.knownUserHasMultipleMuseums = hasMultipleMuseums;
          return hasMultipleMuseums;
        })
        .catch(() => {
          // Fail closed: keep single-museum fallback behavior on lookup errors.
          return false;
        })
        .finally(() => {
          this.knownUserHasMultipleMuseumsPromise = null;
        });
    }
    return this.knownUserHasMultipleMuseumsPromise;
  }

  private stripAuthParams() {
    const params = this.primusRoute.params;
    if (!params?.code && !params?.state) {
      this.authParamsStripped = false;
      return;
    }
    if (this.authParamsStripped) {
      return;
    }
    this.authParamsStripped = true;
    this.router.navigate([], {
      queryParams: {
        code: null,
        state: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // selectMuseum=1 is a temporary URL marker used to force selector mode.
  // Once API auth succeeds, remove it to avoid forcing selector again on later auth/wake transitions.
  private stripMuseumSelectorParam() {
    const params = this.primusRoute.params;
    if (!params?.[AppComponent.MUSEUM_SELECTOR_PARAM]) {
      return;
    }
    this.router.navigate([], {
      queryParams: {
        [AppComponent.MUSEUM_SELECTOR_PARAM]: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  readonly mainmenuV2 = this.featureFlagsService.getFeatureFlags().experimental.useNewMainMenu;
}
